/**
 * 月締め作業(2026-09-24 発注者指示。P-67。仕様は `docs/specs/month-end.md`)。月を選んで ①〜④ の順に進める。
 *  ① 報酬の確定: 人(BB ID)ごとに **日ごと計算**と**月まとめ計算**の両方を出し、交通費・インセンティブ・前払い・調整を足し引きして支給額
 *  ② 歩合のキャンセル: (取得 − キャンセル) × 件数単価。**実キャンセル数の取得元は未確定(P-5)** なので、いまは手入力
 *  ③ 通話料: アジャストワンの請求明細CSVを読み、**発信番号 → 会社**(設定 → 報酬・通話料)で会社ごとに分ける
 *  ④ 請求書: 会社ごとに Σ(その日の納品件数 × その日の会社単価)。**様式は未確定(P-59)** なので一覧とCSVまで
 * URL は `?m=YYYY-MM&step=1..4`。①②の入力と③の取込結果は `month_close/{月}` に保存する。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import NumInput from '@core/components/NumInput';
import { useToast } from '@core/components/Toast';
import { IconChevronLeft, IconChevronRight } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import PageHeader from '@core/components/ui/PageHeader';
import Panel from '@core/components/ui/Panel';
import SegmentedControl from '@core/components/ui/SegmentedControl';
import StatusBadge from '@core/components/ui/StatusBadge';
import { parseCsv, readCsvFile } from '@core/lib/csv';
import { BUSINESS_LABELS, styleLabel, type Company } from '@core/lib/types';
import { aggregateCallCharges, byCompany, type CallChargeRow } from '@core/import/callCharge';
import { CANCEL_BASIS_LABELS, countCancels, type CancelBasis } from '@core/import/cancelReport';
import { downloadCsv } from '@core/masters/csvImport';
import { fetchMonthCancels } from '@core/import/importRepo';
import { fetchCallNumbers, fetchIncentive, labelOfNumber, type CallNumber, type IncentiveSettings } from '@core/masters/settingsPayRepo';
import { DEFAULT_INCENTIVE } from '@core/masters/settingsPayRepo';
import { formatCount, formatCurrency, formatDecimal, formatDuration, formatPercent } from '@core/utils/formatters';
import { addMonths } from './calc';
import { basePayOf, commissionRow, incentiveFor, invoiceRows, monthPayRows, monthPayTotals, payoutOf, type MonthPayRow, type PayBasis } from './monthClose';
import { applyMonthCallCharges, confirmMonthClose, emptyMonthClose, fetchMonthClose, saveMonthCallCharges, saveMonthCancelAuto, saveMonthClose, type MonthClose } from './monthCloseRepo';
import { usePeriodData } from './usePeriod';

const STEPS = [
  { key: 1, label: '① 報酬の確定' },
  { key: 2, label: '② 歩合のキャンセル' },
  { key: 3, label: '③ 通話料' },
  { key: 4, label: '④ 請求書' },
] as const;

const monthLabel = (m: string) => `${m.slice(0, 4)}年${Number(m.slice(5))}月`;

export default function CloseMonthPage() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const period = usePeriodData('month');
  const { rows, companies, deliveries, loading, range, reload } = period;
  const month = range.anchor.slice(0, 7);
  const step = Number(params.get('step') ?? 1);
  const setStep = (s: number) => {
    const next = new URLSearchParams(params);
    next.set('step', String(s));
    setParams(next, { replace: true });
  };
  const setMonth = (m: string) => {
    const next = new URLSearchParams(params);
    next.set('mode', 'month');
    next.set('date', `${m}-01`);
    setParams(next, { replace: true });
  };

  const [close, setClose] = useState<MonthClose>(() => emptyMonthClose(month));
  /** 入力中の値(NumInput は文字列で持つ)。保存のときに数字へ直す */
  const [edits, setEdits] = useState<Record<string, { advance?: string; adjust?: string; cancel?: string }>>({});
  const [incentive, setIncentive] = useState<IncentiveSettings>(DEFAULT_INCENTIVE);
  const [numbers, setNumbers] = useState<CallNumber[]>([]);
  /** 取込済みの対応履歴から数えたキャンセル(BB ID → 件数)。CSVを読み直さなくても出る */
  const [autoCancels, setAutoCancels] = useState<{ by_bb_id: Record<string, number>; total: number } | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const cancelFileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void fetchMonthClose(month)
      .then((v) => {
        setClose(v);
        const next: Record<string, { advance?: string; adjust?: string; cancel?: string }> = {};
        for (const [bb, p] of Object.entries(v.pay)) next[bb] = { advance: p.advance === undefined ? '' : String(p.advance), adjust: p.adjust === undefined ? '' : String(p.adjust) };
        for (const [bb, c] of Object.entries(v.cancels)) next[bb] = { ...next[bb], cancel: String(c) };
        setEdits(next);
        setDirty(false);
      })
      .catch(() => toast('月締めの保存内容を読めませんでした'));
  }, [month, toast]);

  useEffect(() => {
    void fetchIncentive().then(setIncentive).catch(() => undefined);
    void fetchCallNumbers().then((v) => setNumbers(v.items)).catch(() => undefined);
  }, []);

  useEffect(() => {
    setAutoCancels(null);
    void fetchMonthCancels(month)
      .then((v) => setAutoCancels({ by_bb_id: v.by_bb_id, total: v.total }))
      .catch(() => undefined);
  }, [month]);

  // ---- ① 報酬 ----
  const payRows = useMemo(() => {
    const list = monthPayRows(rows);
    const order = ['shinagawa', 'zaitaku', 'timee', 'kanri'];
    return list.sort((a, b) => order.indexOf(a.work_style) - order.indexOf(b.work_style) || a.pay_type.localeCompare(b.pay_type) || a.name.localeCompare(b.name, 'ja'));
  }, [rows]);
  const totals = useMemo(() => monthPayTotals(payRows), [payRows]);
  const basis: PayBasis = close.basis;

  const editOf = (bbId: string) => edits[bbId] ?? {};
  /** 入力中の文字列 → 数字(空・「-」だけのときは 0) */
  const n = (v: string | undefined) => {
    const x = Number(v ?? '');
    return Number.isFinite(x) ? x : 0;
  };
  const setEdit = (bbId: string, patch: { advance?: string; adjust?: string; cancel?: string }) => {
    setEdits((e) => ({ ...e, [bbId]: { ...e[bbId], ...patch } }));
    setDirty(true);
  };

  const incentiveOfRow = useCallback((r: MonthPayRow) => incentiveFor(r, incentive), [incentive]);
  /** 歩合の人は②のキャンセル差引を反映した報酬を使う */
  const baseOf = useCallback(
    (r: MonthPayRow) => (r.pay_type === 'commission' ? commissionRow(r, n(editOf(r.bb_id).cancel)).pay : basePayOf(r, basis)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [basis, edits],
  );
  const payoutRowOf = useCallback(
    (r: MonthPayRow) => payoutOf({ base: baseOf(r), commute: r.commute, incentive: incentiveOfRow(r), advance: n(editOf(r.bb_id).advance), adjust: n(editOf(r.bb_id).adjust) }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [baseOf, incentiveOfRow, edits],
  );
  const payoutTotal = useMemo(() => payRows.reduce((a, r) => a + payoutRowOf(r), 0), [payRows, payoutRowOf]);

  const commissionRows = useMemo(() => payRows.filter((r) => r.pay_type === 'commission'), [payRows]);

  /** 実キャンセル: CSVを読み込んでいればその値、無ければ取込済みの対応履歴から数えた値 */
  const realCancelOf = (bbId: string): number | null => {
    if (close.cancels_auto) return close.cancels_auto.by_bb_id[bbId] ?? 0;
    if (autoCancels) return autoCancels.by_bb_id[bbId] ?? 0;
    return null;
  };

  // ---- ④ 請求書 ----
  const invoices = useMemo(() => invoiceRows(companies, deliveries), [companies, deliveries]);
  const invoiceTotal = useMemo(() => invoices.reduce((a, r) => a + r.amount, 0), [invoices]);
  const [openInvoice, setOpenInvoice] = useState<string | null>(null);

  // ---- ③ 通話料 ----
  const callRows: CallChargeRow[] = close.calls?.rows ?? [];
  const callByCompany = useMemo(() => byCompany(callRows), [callRows]);
  const companyMap = useMemo(() => new Map(companies.map((c: Company) => [c.id, c])), [companies]);
  /** 会社ごとの取得件数(1アポあたりの通話時間を出すため) */
  const apptByCompany = useMemo(() => {
    const m = new Map<string, number>();
    for (const r of rows) if (r.company_id) m.set(r.company_id, (m.get(r.company_id) ?? 0) + r.appointments);
    return m;
  }, [rows]);

  const onPickFile = async (file: File) => {
    setBusy(true);
    try {
      const text = await readCsvFile(file);
      const table = parseCsv(text);
      const list = table.rows.map((cells) => Object.fromEntries(table.headers.map((h, i) => [h.trim(), cells[i] ?? ''])));
      const result = aggregateCallCharges(list, numbers, month);
      if (result.total_rows === 0) {
        toast(`${monthLabel(month)} の通話がこのファイルにありません(列名・利用月を確かめてください)`);
        return;
      }
      await saveMonthCallCharges(
        month,
        {
          file_name: file.name,
          total_rows: result.total_rows,
          total_sec: result.total_sec,
          total_yen: result.total_yen,
          rows: result.rows,
          by_number: result.by_number,
          unknown_numbers: result.unknown_numbers,
        },
        user?.id ?? '',
      );
      setClose(await fetchMonthClose(month));
      toast(`通話料を取り込みました(${formatCount(result.total_rows, { unit: '件' })} ／ ${formatCurrency(Math.round(result.total_yen))})`);
    } catch (e) {
      toast(e instanceof Error ? e.message : '通話料のCSVを読めませんでした');
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  /** ②: 顧客情報レポートCSV(複数可。会社ごとに1つ)から「対応者情報=キャンセル」を数える */
  const onPickCancelFiles = async (files: FileList) => {
    setBusy(true);
    try {
      const basis: CancelBasis = close.cancels_auto?.basis ?? 'created';
      const byBbId: Record<string, number> = {};
      const unknown = new Map<string, number>();
      const businesses = new Set<string>();
      const names: string[] = [];
      let total = 0;
      let noColumn = 0;
      for (const file of Array.from(files)) {
        const text = await readCsvFile(file);
        const table = parseCsv(text);
        const list = table.rows.map((cells) => Object.fromEntries(table.headers.map((h, i) => [h.trim(), cells[i] ?? ''])));
        const r = countCancels(list, month, basis);
        if (r.no_column) {
          noColumn++;
          continue;
        }
        names.push(file.name);
        total += r.total;
        for (const [bb, n2] of Object.entries(r.by_bb_id)) byBbId[bb] = (byBbId[bb] ?? 0) + n2;
        for (const u of r.unknown) unknown.set(u.name, (unknown.get(u.name) ?? 0) + u.count);
        for (const b of r.businesses) businesses.add(b);
      }
      if (noColumn > 0 && names.length === 0) {
        toast('「対応者情報」の列がありません。顧客情報レポートを**次アクションで絞らずに**出してください');
        return;
      }
      await saveMonthCancelAuto(
        month,
        { basis, files: names, by_bb_id: byBbId, unknown: [...unknown.entries()].map(([name, count]) => ({ name, count })), total, businesses: [...businesses] },
        user?.id ?? '',
      );
      setClose(await fetchMonthClose(month));
      toast(`キャンセルを読み込みました(${formatCount(total, { unit: '件' })} ／ ${names.length}ファイル)`);
    } catch (e) {
      toast(e instanceof Error ? e.message : 'キャンセルのCSVを読めませんでした');
    } finally {
      setBusy(false);
      if (cancelFileRef.current) cancelFileRef.current.value = '';
    }
  };

  /** 実キャンセル(自動)を「キャンセル(入力)」へまとめて入れる */
  const applyAutoCancels = () => {
    const auto = close.cancels_auto?.by_bb_id ?? autoCancels?.by_bb_id ?? {};
    setEdits((e) => {
      const next = { ...e };
      for (const [bb, v] of Object.entries(auto)) next[bb] = { ...next[bb], cancel: String(v) };
      return next;
    });
    setDirty(true);
    toast('読み込んだ件数を入力欄に入れました。保存すると反映されます');
  };

  const save = async (nextStep?: number) => {
    if (!isAdmin) return;
    setBusy(true);
    try {
      const pay: MonthClose['pay'] = {};
      const cancels: MonthClose['cancels'] = {};
      for (const [bb, e] of Object.entries(edits)) {
        const advance = e.advance === undefined || e.advance === '' ? undefined : n(e.advance);
        const adjust = e.adjust === undefined || e.adjust === '' ? undefined : n(e.adjust);
        if (advance !== undefined || adjust !== undefined) pay[bb] = { ...(advance !== undefined ? { advance } : {}), ...(adjust !== undefined ? { adjust } : {}) };
        if (e.cancel !== undefined && e.cancel !== '') cancels[bb] = n(e.cancel);
      }
      await saveMonthClose(month, { basis: close.basis, pay, cancels }, user?.id ?? '');
      setClose((c) => ({ ...c, pay, cancels }));
      setDirty(false);
      toast('保存しました');
      if (nextStep) setStep(nextStep);
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存できませんでした');
    } finally {
      setBusy(false);
    }
  };

  const downloadInvoiceCsv = () => {
    const head = '会社ID,会社名,納品件数,単価,金額\n';
    const body = invoices
      .map((r) => [r.company_id, `"${r.name}"`, r.count, r.prices.join(';'), r.amount].join(','))
      .join('\n');
    downloadCsv(`請求_${month}.csv`, `${head}${body}\n合計,,${invoices.reduce((a, r) => a + r.count, 0)},,${invoiceTotal}\n`);
  };

  const downloadPayCsv = () => {
    const head = 'BB ID,名前,形態,架電内容,単価,日数,稼働h(日ごと),稼働h(丸め前),取得件数,報酬(日ごと),報酬(月まとめ),交通費,インセンティブ,前払い,調整,支給額\n';
    const body = payRows
      .map((r) =>
        [
          r.bb_id,
          `"${r.name}"`,
          styleLabel(r.work_style, r.pay_type),
          BUSINESS_LABELS[r.business],
          r.wage,
          r.days,
          r.hours_daily,
          r.hours_raw,
          r.appointments,
          r.pay_daily,
          r.pay_month,
          r.commute,
          incentiveOfRow(r),
          n(editOf(r.bb_id).advance),
          n(editOf(r.bb_id).adjust),
          payoutRowOf(r),
        ].join(','),
      )
      .join('\n');
    downloadCsv(`報酬_${month}.csv`, `${head}${body}\n`);
  };

  const confirmed = !!close.confirmed_at;

  return (
    <div className="screen full">
      <PageHeader title="月締め作業" meta={monthLabel(month)} />
      <FilterBar label="締める月">
        <span className="ui-date-nav">
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, -1))} aria-label="前の月">
            <IconChevronLeft size={16} />
          </button>
          <input type="month" className="ui-input" aria-label="月" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} />
          <button type="button" className="ui-btn ui-btn-secondary ui-btn-icon" onClick={() => setMonth(addMonths(month, 1))} aria-label="次の月">
            <IconChevronRight size={16} />
          </button>
        </span>
        <span className="close-steps">
          {STEPS.map((s) => (
            <button key={s.key} type="button" className={`ui-btn ui-btn-sm ${step === s.key ? 'ui-btn-primary' : 'ui-btn-secondary'}`} onClick={() => setStep(s.key)}>
              {s.label}
            </button>
          ))}
        </span>
        {confirmed && <StatusBadge kind="confirmed" label="確定済み" />}
        {dirty && <StatusBadge kind="notEntered" label="未保存" />}
      </FilterBar>

      {loading ? (
        <div className="ui-panel">
          <div className="ui-empty">読み込み中…</div>
        </div>
      ) : step === 1 ? (
        <>
          <Panel
            title={`① 報酬の確定(${monthLabel(month)})`}
            meta={`${formatCount(payRows.length, { unit: '名' })} ／ 支給額の合計 ${formatCurrency(payoutTotal)}`}
            note="**日ごと**はいまのシステムの計算(PL・日次【集計】と同じ値)、**月まとめ**は「丸める前の稼働時間 × 時給」(月の途中で時給が変わった人は単価ごとに計算)です。**どちらを支払いの正にするかは未確定(P-1)** なので、上の「支払いの基準」で選んだ方で支給額を出します。歩合の人は②のキャンセルを引いた報酬です"
            actions={
              <>
                <SegmentedControl
                  label="支払いの基準"
                  showLabel
                  size="sm"
                  options={[
                    { key: 'daily', label: '日ごと' },
                    { key: 'month', label: '月まとめ' },
                  ]}
                  value={basis}
                  onChange={(v) => {
                    setClose((c) => ({ ...c, basis: v as PayBasis }));
                    setDirty(true);
                  }}
                />
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={downloadPayCsv}>
                  CSVで出す
                </button>
              </>
            }
          >
            <div className="month-close-summary">
              <span>
                日ごとの合計 <b>{formatCurrency(totals.pay_daily)}</b>
              </span>
              <span>
                月まとめの合計 <b>{formatCurrency(totals.pay_month)}</b>
              </span>
              <span className={totals.diff === 0 ? '' : totals.diff > 0 ? 'is-plus' : 'is-minus'}>
                差 <b>{totals.diff > 0 ? '+' : ''}{formatCurrency(totals.diff)}</b>
              </span>
              <span>
                稼働 <b>{formatDecimal(totals.hours_daily, { digits: 1 })}h</b> ／ 丸める前 <b>{formatDecimal(totals.hours_raw, { digits: 1 })}h</b>
              </span>
              <span>
                交通費 <b>{formatCurrency(totals.commute)}</b>
              </span>
            </div>
            <div className="report-scroll is-auto">
              <table className="report-table is-wide">
                <thead>
                  <tr>
                    <th scope="col">スタッフ名</th>
                    <th scope="col">形態</th>
                    <th scope="col">架電内容</th>
                    <th scope="col" className="is-num">単価</th>
                    <th scope="col" className="is-num">日数</th>
                    <th scope="col" className="is-num">稼働h(日ごと)</th>
                    <th scope="col" className="is-num">稼働h(丸め前)</th>
                    <th scope="col" className="is-num">取得</th>
                    <th scope="col" className="is-num">報酬(日ごと)</th>
                    <th scope="col" className="is-num">報酬(月まとめ)</th>
                    <th scope="col" className="is-num">差</th>
                    <th scope="col" className="is-num">交通費</th>
                    <th scope="col" className="is-num">インセンティブ</th>
                    <th scope="col" className="is-num">前払い</th>
                    <th scope="col" className="is-num">調整</th>
                    <th scope="col" className="is-num">支給額</th>
                  </tr>
                </thead>
                <tbody>
                  {payRows.map((r) => {
                    const diff = r.pay_month - r.pay_daily;
                    const inp = editOf(r.bb_id);
                    return (
                      <tr key={r.bb_id}>
                        <th scope="row">
                          {r.name}
                          <span className="cell-sub">{r.bb_id}</span>
                        </th>
                        <td>{styleLabel(r.work_style, r.pay_type)}</td>
                        <td>{BUSINESS_LABELS[r.business]}</td>
                        <td className="is-num">{formatCurrency(r.wage)}</td>
                        <td className="is-num">{formatCount(r.days)}</td>
                        <td className="is-num">{formatDecimal(r.hours_daily, { digits: 1 })}</td>
                        <td className="is-num">{formatDecimal(r.hours_raw, { digits: 1 })}</td>
                        <td className="is-num">{formatCount(r.appointments)}</td>
                        <td className={`is-num${basis === 'daily' ? ' col-total' : ''}`}>{formatCurrency(r.pay_daily)}</td>
                        <td className={`is-num${basis === 'month' ? ' col-total' : ''}`}>{formatCurrency(r.pay_month)}</td>
                        <td className={`is-num ${diff === 0 ? '' : diff > 0 ? 'pos' : 'neg'}`}>{diff === 0 ? '—' : `${diff > 0 ? '+' : ''}${formatCurrency(diff)}`}</td>
                        <td className="is-num">{r.commute > 0 ? formatCurrency(r.commute) : <span className="cell-na">—</span>}</td>
                        <td className="is-num">{incentiveOfRow(r) > 0 ? formatCurrency(incentiveOfRow(r)) : <span className="cell-na">—</span>}</td>
                        <td className="is-num">
                          {isAdmin ? (
                            <NumInput className="close-num" value={inp.advance ?? ''} onChange={(v) => setEdit(r.bb_id, { advance: v })} placeholder="0" aria-label={`${r.name} の前払い`} />
                          ) : (
                            formatCurrency(n(inp.advance))
                          )}
                        </td>
                        <td className="is-num">
                          {isAdmin ? (
                            <NumInput className="close-num" value={inp.adjust ?? ''} onChange={(v) => setEdit(r.bb_id, { adjust: v })} placeholder="0" aria-label={`${r.name} の調整`} />
                          ) : (
                            formatCurrency(n(inp.adjust))
                          )}
                        </td>
                        <td className="is-num col-total">{formatCurrency(payoutRowOf(r))}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row">合計</th>
                    <td colSpan={4} />
                    <td className="is-num">{formatDecimal(totals.hours_daily, { digits: 1 })}</td>
                    <td className="is-num">{formatDecimal(totals.hours_raw, { digits: 1 })}</td>
                    <td className="is-num">{formatCount(totals.appointments)}</td>
                    <td className="is-num">{formatCurrency(totals.pay_daily)}</td>
                    <td className="is-num">{formatCurrency(totals.pay_month)}</td>
                    <td className="is-num">{totals.diff > 0 ? '+' : ''}{formatCurrency(totals.diff)}</td>
                    <td className="is-num">{formatCurrency(totals.commute)}</td>
                    <td colSpan={3} />
                    <td className="is-num col-total">{formatCurrency(payoutTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
            <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
              <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || !isAdmin} onClick={() => void save()}>
                保存する
              </button>
              <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !isAdmin} onClick={() => void save(2)}>
                保存して次へ(② 歩合のキャンセル)
              </button>
            </div>
          </Panel>
        </>
      ) : step === 2 ? (
        <Panel
          title={`② 歩合のキャンセル(${monthLabel(month)})`}
          meta={`${formatCount(commissionRows.length, { unit: '名' })}`}
          note="**歩合の人だけ**です。報酬 = (取得件数 − キャンセル件数) × 件数単価。**実キャンセルは、毎日取り込んでいる対応履歴から自動で出ます**(対応者情報=「キャンセル」。**リアクションは「アポ〇」のままなので取得件数は減りません**)。取込がまだの月は、顧客情報レポートCSV(次アクションで絞らずに出したもの。会社ごとにまとめて選べます)を読むこともできます。入れた件数は①の報酬にも反映されます"
          actions={
            <>
              <SegmentedControl
                label="キャンセルの月"
                showLabel
                size="sm"
                options={(Object.keys(CANCEL_BASIS_LABELS) as CancelBasis[]).map((k) => ({ key: k, label: CANCEL_BASIS_LABELS[k] }))}
                value={close.cancels_auto?.basis ?? 'created'}
                onChange={(v) => setClose((c) => ({ ...c, cancels_auto: { ...(c.cancels_auto ?? { files: [], by_bb_id: {}, unknown: [], total: 0, businesses: [] }), basis: v as CancelBasis } }))}
              />
              <input
                ref={cancelFileRef}
                type="file"
                accept=".csv,text/csv"
                multiple
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files;
                  if (f && f.length > 0) void onPickCancelFiles(f);
                }}
              />
              <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={busy || !isAdmin} onClick={() => cancelFileRef.current?.click()}>
                顧客情報レポートCSVを読む
              </button>
              {((close.cancels_auto?.total ?? 0) > 0 || (autoCancels?.total ?? 0) > 0) && (
                <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={busy || !isAdmin} onClick={applyAutoCancels}>
                  実キャンセルを入力欄に入れる
                </button>
              )}
            </>
          }
        >
          {commissionRows.length === 0 ? (
            <div className="ui-empty">この月は歩合の稼働がありません</div>
          ) : (
            <>
              {(close.cancels_auto || autoCancels) && (
                <div className="month-close-summary">
                  <span>
                    実キャンセル <b>{formatCount(close.cancels_auto ? close.cancels_auto.total : (autoCancels?.total ?? 0), { unit: '件' })}</b>
                  </span>
                  <span>
                    出どころ <b>{close.cancels_auto ? `CSV(${CANCEL_BASIS_LABELS[close.cancels_auto.basis]})` : '取込済みの対応履歴'}</b>
                  </span>
                  {(close.cancels_auto?.businesses.length ?? 0) > 0 && <span>業務: {close.cancels_auto!.businesses.join(' / ')}</span>}
                  {(close.cancels_auto?.unknown.length ?? 0) > 0 && (
                    <span className="is-minus">
                      BB IDが読めない行 <b>{formatCount(close.cancels_auto!.unknown.reduce((a, u) => a + u.count, 0), { unit: '件' })}</b>(
                      {close.cancels_auto!.unknown.map((u) => `${u.name}:${u.count}`).join('・')})
                    </span>
                  )}
                </div>
              )}
              <div className="report-scroll is-auto">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th scope="col">スタッフ名</th>
                      <th scope="col">架電内容</th>
                      <th scope="col" className="is-num">件数単価</th>
                      <th scope="col" className="is-num">取得件数</th>
                      <th scope="col" className="is-num">実キャンセル</th>
                      <th scope="col" className="is-num">キャンセル(入力)</th>
                      <th scope="col" className="is-num">キャンセル率</th>
                      <th scope="col" className="is-num">差引後の件数</th>
                      <th scope="col" className="is-num">差引前の報酬</th>
                      <th scope="col" className="is-num">差引後の報酬</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissionRows.map((r) => {
                      const c = commissionRow(r, n(editOf(r.bb_id).cancel));
                      return (
                        <tr key={r.bb_id}>
                          <th scope="row">
                            {r.name}
                            <span className="cell-sub">{r.bb_id}</span>
                          </th>
                          <td>{BUSINESS_LABELS[r.business]}</td>
                          <td className="is-num">{formatCurrency(r.wage)}</td>
                          <td className="is-num">{formatCount(r.appointments)}</td>
                          <td className="is-num">
                            {realCancelOf(r.bb_id) === null ? (
                              <span className="cell-na" title="対応履歴の取込がまだです">—</span>
                            ) : realCancelOf(r.bb_id)! > 0 ? (
                              formatCount(realCancelOf(r.bb_id)!)
                            ) : (
                              <span className="cell-na">0</span>
                            )}
                          </td>
                          <td className="is-num">
                            {isAdmin ? (
                              <NumInput className="close-num" value={editOf(r.bb_id).cancel ?? ''} onChange={(v) => setEdit(r.bb_id, { cancel: v })} placeholder="0" aria-label={`${r.name} のキャンセル件数`} />
                            ) : (
                              formatCount(n(editOf(r.bb_id).cancel))
                            )}
                          </td>
                          <td className="is-num">{c.cancels > 0 ? formatPercent(c.cancel_rate, { digits: 1 }) : <span className="cell-na">—</span>}</td>
                          <td className="is-num">{formatCount(c.net)}</td>
                          <td className="is-num">{formatCurrency(r.pay_daily)}</td>
                          <td className="is-num col-total">{formatCurrency(c.pay)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="button" className="ui-btn ui-btn-secondary" disabled={busy || !isAdmin} onClick={() => void save()}>
                  保存する
                </button>
                <button type="button" className="ui-btn ui-btn-primary" disabled={busy || !isAdmin} onClick={() => void save(3)}>
                  保存して次へ(③ 通話料)
                </button>
              </div>
            </>
          )}
        </Panel>
      ) : step === 3 ? (
        <Panel
          title={`③ 通話料(${monthLabel(month)})`}
          meta={close.calls ? `${close.calls.file_name} ／ ${formatCount(close.calls.total_rows, { unit: '件' })} ／ ${formatCurrency(Math.round(close.calls.total_yen))}${close.calls.applied ? ' ／ PLに反映中' : ' ／ PLは推定のまま'}` : '未取込'}
          note="**アジャストワンの請求明細CSV**(`cdr_…csv`)を選ぶと、**ご利用番号 → 会社**(設定 → 報酬・通話料)でその月の通話を分けます。**顧客の電話番号は保存しません**(番号×日の合計だけ)。**PLの通話料は「PLに反映する(確定)」を押すまで変わりません**(押すと 会社別＝実額・会社に紐づかない番号は「その他(CC事業部以外)」として出ます)"
          actions={
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) void onPickFile(f);
                }}
              />
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" disabled={busy || !isAdmin} onClick={() => fileRef.current?.click()}>
                CSVを選ぶ
              </button>
              {close.calls && (
                <button
                  type="button"
                  className={`ui-btn ui-btn-sm ${close.calls.applied ? 'ui-btn-secondary' : 'ui-btn-primary'}`}
                  disabled={busy || !isAdmin}
                  onClick={() => {
                    const next = !close.calls?.applied;
                    setBusy(true);
                    void applyMonthCallCharges(month, next, user?.id ?? '')
                      .then(() => fetchMonthClose(month))
                      .then((v) => {
                        setClose(v);
                        toast(next ? 'PLの通話料をこの実績にしました' : 'PLの通話料を推定に戻しました');
                      })
                      .catch((e) => toast(e instanceof Error ? e.message : '変更できませんでした'))
                      .finally(() => setBusy(false));
                  }}
                >
                  {close.calls.applied ? 'PLへの反映をやめる' : 'PLに反映する(確定)'}
                </button>
              )}
            </>
          }
        >
          {!close.calls ? (
            <div className="ui-empty">まだ取り込んでいません。「CSVを選ぶ」からアジャストワンの請求明細CSVを読み込んでください</div>
          ) : (
            <>
              <div className="month-close-summary">
                <span>
                  通話 <b>{formatCount(close.calls.total_rows, { unit: '件' })}</b>
                </span>
                <span>
                  通話時間 <b>{formatDecimal(close.calls.total_sec / 3600, { digits: 1 })}h</b>
                </span>
                <span>
                  通話料 <b>{formatCurrency(Math.round(close.calls.total_yen))}</b>
                </span>
                {close.calls.unknown_numbers.length > 0 && (
                  <span className="is-minus">
                    会社の決まらない番号 <b>{formatCount(close.calls.unknown_numbers.length, { unit: '件' })}</b>
                  </span>
                )}
              </div>
              <div className="report-scroll is-auto">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th scope="col">会社</th>
                      <th scope="col" className="is-num">通話回数</th>
                      <th scope="col" className="is-num">通話時間</th>
                      <th scope="col" className="is-num">通話料</th>
                      <th scope="col" className="is-num">取得件数</th>
                      <th scope="col" className="is-num">1アポあたりの通話時間</th>
                      <th scope="col" className="is-num">1アポあたりの通話料</th>
                    </tr>
                  </thead>
                  <tbody>
                    {callByCompany.map((c) => {
                      const appts = c.company_id ? (apptByCompany.get(c.company_id) ?? 0) : 0;
                      return (
                        <tr key={c.company_id ?? 'none'}>
                          <th scope="row">{c.company_id ? (companyMap.get(c.company_id)?.name ?? c.company_id) : '会社が決まらない番号'}</th>
                          <td className="is-num">{formatCount(c.calls)}</td>
                          <td className="is-num">{formatDecimal(c.sec / 3600, { digits: 1 })}h</td>
                          <td className="is-num">{formatCurrency(Math.round(c.yen))}</td>
                          <td className="is-num">{appts > 0 ? formatCount(appts) : <span className="cell-na">—</span>}</td>
                          <td className="is-num">{appts > 0 ? formatDuration(Math.round(c.sec / appts)) : <span className="cell-na">—</span>}</td>
                          <td className="is-num">{appts > 0 ? formatCurrency(Math.round(c.yen / appts)) : <span className="cell-na">—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {(close.calls.by_number?.length ?? 0) > 0 && (
                <div className="report-scroll is-auto" style={{ marginTop: 'var(--space-4)' }}>
                  <table className="report-table">
                    <caption className="ui-hint">発信番号(用途)別の内訳。**会社に紐づく番号**は会社の通話料、**それ以外**は「CC事業部以外」です(設定 → 報酬・通話料 で決めます)</caption>
                    <thead>
                      <tr>
                        <th scope="col">発信番号</th>
                        <th scope="col">用途・会社</th>
                        <th scope="col">区分</th>
                        <th scope="col" className="is-num">通話回数</th>
                        <th scope="col" className="is-num">通話時間</th>
                        <th scope="col" className="is-num">通話料</th>
                      </tr>
                    </thead>
                    <tbody>
                      {close.calls.by_number!.map((n2) => {
                        const label = labelOfNumber(numbers, n2.number);
                        const company = n2.company_id ? (companyMap.get(n2.company_id)?.name ?? n2.company_id) : null;
                        return (
                          <tr key={n2.number}>
                            <th scope="row">{n2.number}</th>
                            <td>{label || company || <span className="cell-na">未登録</span>}</td>
                            <td>{company ? <span className="tag-company">会社</span> : <span className="tag-other">CC事業部以外</span>}</td>
                            <td className="is-num">{formatCount(n2.calls)}</td>
                            <td className="is-num">{formatDecimal(n2.sec / 3600, { digits: 1 })}h</td>
                            <td className="is-num">{formatCurrency(Math.round(n2.yen))}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <th scope="row">会社ぶんの合計</th>
                        <td colSpan={2} />
                        <td className="is-num">{formatCount(close.calls.by_number!.filter((x) => x.company_id).reduce((a, x) => a + x.calls, 0))}</td>
                        <td className="is-num">{formatDecimal(close.calls.by_number!.filter((x) => x.company_id).reduce((a, x) => a + x.sec, 0) / 3600, { digits: 1 })}h</td>
                        <td className="is-num col-total">{formatCurrency(Math.round(close.calls.by_number!.filter((x) => x.company_id).reduce((a, x) => a + x.yen, 0)))}</td>
                      </tr>
                      <tr>
                        <th scope="row">CC事業部以外の合計</th>
                        <td colSpan={2} />
                        <td className="is-num">{formatCount(close.calls.by_number!.filter((x) => !x.company_id).reduce((a, x) => a + x.calls, 0))}</td>
                        <td className="is-num">{formatDecimal(close.calls.by_number!.filter((x) => !x.company_id).reduce((a, x) => a + x.sec, 0) / 3600, { digits: 1 })}h</td>
                        <td className="is-num">{formatCurrency(Math.round(close.calls.by_number!.filter((x) => !x.company_id).reduce((a, x) => a + x.yen, 0)))}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
              {close.calls.unknown_numbers.length > 0 && (
                <div className="report-scroll is-auto" style={{ marginTop: 'var(--space-4)' }}>
                  <table className="report-table">
                    <caption className="ui-hint">会社が決まらない発信番号(設定 → 報酬・通話料 で会社を割り当てると、次の取込から分かれます)</caption>
                    <thead>
                      <tr>
                        <th scope="col">発信番号</th>
                        <th scope="col" className="is-num">通話回数</th>
                        <th scope="col" className="is-num">通話料</th>
                      </tr>
                    </thead>
                    <tbody>
                      {close.calls.unknown_numbers.map((u) => (
                        <tr key={u.number}>
                          <th scope="row">{u.number}</th>
                          <td className="is-num">{formatCount(u.calls)}</td>
                          <td className="is-num">{formatCurrency(Math.round(u.yen))}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                <button type="button" className="ui-btn ui-btn-primary" onClick={() => setStep(4)}>
                  次へ(④ 請求書)
                </button>
              </div>
            </>
          )}
        </Panel>
      ) : (
        <Panel
          title={`④ 請求書(${monthLabel(month)})`}
          meta={`${formatCount(invoices.length, { unit: '社' })} ／ 合計 ${formatCurrency(invoiceTotal)}`}
          note="**金額 = Σ(その日の納品件数 × その日の会社単価)** です(単価が月の途中で変わってもその日の単価で計算します)。**請求書の様式は未確定(P-59)** のため、いまは一覧とCSVまでです。会社の行を押すと日ごとの明細が開きます"
          actions={
            <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={downloadInvoiceCsv}>
              CSVで出す
            </button>
          }
        >
          {invoices.length === 0 ? (
            <div className="ui-empty">この月の納品件数がまだ入っていません(締め作業① で入れます)</div>
          ) : (
            <>
              <div className="report-scroll is-auto">
                <table className="report-table">
                  <thead>
                    <tr>
                      <th scope="col">会社</th>
                      <th scope="col" className="is-num">納品件数</th>
                      <th scope="col" className="is-num">単価</th>
                      <th scope="col" className="is-num">金額</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((r) => (
                      <Fragment key={r.company_id}>
                        <tr className="is-clickable" onClick={() => setOpenInvoice(openInvoice === r.company_id ? null : r.company_id)}>
                          <th scope="row">
                            {r.name}
                            <span className="cell-sub">{r.company_id}</span>
                          </th>
                          <td className="is-num">{formatCount(r.count)}</td>
                          <td className="is-num">{r.prices.map((p) => formatCurrency(p)).join(' / ')}</td>
                          <td className="is-num col-total">{formatCurrency(r.amount)}</td>
                        </tr>
                        {openInvoice === r.company_id &&
                          r.days.map((d) => (
                            <tr key={`${r.company_id}-${d.date}`} className="is-child">
                              <th scope="row" style={{ paddingLeft: 'var(--space-6)' }}>
                                {d.date.slice(5).replace('-', '/')}
                              </th>
                              <td className="is-num">{formatCount(d.count)}</td>
                              <td className="is-num">{formatCurrency(d.price)}</td>
                              <td className="is-num">{formatCurrency(d.amount)}</td>
                            </tr>
                          ))}
                      </Fragment>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th scope="row">合計</th>
                      <td className="is-num">{formatCount(invoices.reduce((a, r) => a + r.count, 0))}</td>
                      <td />
                      <td className="is-num col-total">{formatCurrency(invoiceTotal)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="modal-actions" style={{ justifyContent: 'flex-start' }}>
                <button
                  type="button"
                  className="ui-btn ui-btn-primary"
                  disabled={busy || !isAdmin || confirmed}
                  onClick={() => {
                    setBusy(true);
                    void confirmMonthClose(month, user?.id ?? '')
                      .then(() => fetchMonthClose(month))
                      .then((v) => {
                        setClose(v);
                        toast(`${monthLabel(month)} を確定しました`);
                        void reload();
                      })
                      .catch((e) => toast(e instanceof Error ? e.message : '確定できませんでした'))
                      .finally(() => setBusy(false));
                  }}
                >
                  {confirmed ? '確定済み' : 'この月を確定する'}
                </button>
              </div>
            </>
          )}
        </Panel>
      )}
    </div>
  );
}
